import { useEffect, useState } from 'react';
import { Search, X, RefreshCw } from 'lucide-react';
import { SortableTableHead, formatInTimezone, useTimezone } from '@kioskgaming/ui';
import { getTableSortColumns } from '../../../config/tableSortConfig';
import useTableSort from '../../../hooks/useTableSort';
import { getShopGameWallets } from '../../../services/api';
import type { ShopGameWallet } from '../../../types';
import GameWalletDrawer from '../GameWalletDrawer';
import { GAME_WALLET_SYNC_LABELS } from '../../../utils/gameWalletSync';

const SYNC_LABELS = GAME_WALLET_SYNC_LABELS;

const SYNC_BADGE: Record<ShopGameWallet['syncStatus'], string> = {
  in_sync: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  never_synced: 'bg-amber-50 text-amber-700 border-amber-200',
};

/** Match portal ledger tables — one class for static and sortable headers. */
const TABLE_HEAD = 'h-auto px-3 py-3 text-xs font-semibold text-stone-500';
const SORT_TABLE_HEAD = `${TABLE_HEAD} [&_button]:!text-stone-500 [&_button]:text-xs [&_button]:font-semibold hover:[&_button]:!text-stone-700`;

interface Props {
  providerId: string;
  providerName?: string;
}

export default function WalletsTab({ providerId, providerName }: Props) {
  const { timezone } = useTimezone();
  const [wallets, setWallets] = useState<ShopGameWallet[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [syncStatus, setSyncStatus] = useState('');
  const [page, setPage] = useState(1);
  const [selectedWallet, setSelectedWallet] = useState<ShopGameWallet | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const limit = 20;

  const tableSort = useTableSort({
    columns: getTableSortColumns('shopGameWallets'),
    onChange: () => setPage(1),
  });

  const headerSort = {
    activeKey: tableSort.sortBy,
    direction: tableSort.sortOrder,
  };
  const isTableBusy = loading;

  function fetchWallets() {
    setLoading(true);
    setError(null);
    getShopGameWallets({
      providerId,
      search: search || undefined,
      syncStatus: syncStatus || undefined,
      page,
      limit,
      ...tableSort.sortParams,
    })
      .then((res) => {
        if (res.success && res.data) {
          setWallets(res.data.items);
          setTotal(res.data.total);
        } else {
          setError(res.message || 'Failed to load wallets');
        }
      })
      .catch(() => setError('Failed to load wallets'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchWallets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, search, syncStatus, page, tableSort.sortBy, tableSort.sortOrder]);

  const totalPages = Math.ceil(total / limit);

  function openDrawer(wallet: ShopGameWallet) {
    setSelectedWallet(wallet);
    setDrawerOpen(true);
  }

  return (
    <>
      <div className="space-y-4">
        {/* Section header */}
        <div>
          <h2 className="text-xl font-bold tracking-tight text-stone-900">Wallets</h2>
          <p className="text-xs text-stone-500 mt-1">
            Use this tab to investigate Player game wallet balances and sync status.
          </p>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-stone-500 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <input
                type="text"
                placeholder="Player phone, email, username…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="w-full h-9 rounded-lg border border-stone-200 pl-9 pr-8 text-sm text-stone-900 outline-none placeholder:text-stone-400 focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => { setSearch(''); setPage(1); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="min-w-[160px]">
            <label className="block text-xs font-medium text-stone-500 mb-1">Sync status</label>
            <select
              value={syncStatus}
              onChange={(e) => { setSyncStatus(e.target.value); setPage(1); }}
              className="w-full h-9 rounded-lg border border-stone-200 px-3 text-sm text-stone-700 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
            >
              <option value="">All statuses</option>
              <option value="in_sync">In sync</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="never_synced">Never synced</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setSearch(''); setSyncStatus(''); setPage(1); }}
              className="h-9 inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-3 text-sm font-medium text-stone-600 hover:bg-stone-50"
            >
              <X className="h-4 w-4" />
              Clear
            </button>
            <button
              type="button"
              onClick={fetchWallets}
              disabled={loading}
              className="h-9 inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-3 text-sm font-medium text-stone-600 hover:bg-stone-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Table */}
        {loading && <p className="py-10 text-center text-sm text-stone-400">Loading…</p>}
        {error && <p className="py-8 text-center text-sm text-red-600">{error}</p>}

        {!loading && !error && (
          <div className="overflow-x-auto rounded-lg border border-stone-200" aria-busy={isTableBusy || undefined}>
            <table className="w-full min-w-[780px]">
              <thead>
                <tr className="border-b border-stone-100 bg-stone-50">
                  <th className={`${TABLE_HEAD} text-left`}>Player</th>
                  <th className={`${TABLE_HEAD} text-left`}>Game wallet ID</th>
                  <SortableTableHead
                    sortable
                    label="Temp wallet balance"
                    sortKey="walletBalance"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                    align="right"
                    disabled={isTableBusy}
                    className={SORT_TABLE_HEAD}
                  />
                  <SortableTableHead
                    sortable
                    label="In-game balance"
                    sortKey="inGameBalance"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                    align="right"
                    disabled={isTableBusy}
                    className={SORT_TABLE_HEAD}
                  />
                  <th className={`${TABLE_HEAD} text-left`}>Sync status</th>
                  <SortableTableHead
                    sortable
                    label="Last sync"
                    sortKey="lastSyncedAt"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                    disabled={isTableBusy}
                    className={SORT_TABLE_HEAD}
                  />
                  <th className={`${TABLE_HEAD} text-center`}>Open</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {wallets.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-sm text-stone-400">
                      No wallets found
                    </td>
                  </tr>
                )}
                {wallets.map((wallet) => (
                  <tr
                    key={wallet.id}
                    onClick={() => openDrawer(wallet)}
                    className="cursor-pointer hover:bg-stone-50"
                  >
                    <td className="px-3 py-3">
                      <div className="text-sm font-medium text-stone-900">{wallet.playerName}</div>
                      {wallet.playerUsername && wallet.playerUsername !== wallet.playerName && (
                        <div className="text-xs text-stone-400">{wallet.playerUsername}</div>
                      )}
                    </td>
                    <td className="px-3 py-3 max-w-[220px]">
                      <span
                        className="font-mono text-xs text-stone-500 break-all"
                        title={wallet.gameWalletId}
                      >
                        {wallet.gameWalletId}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-sm text-stone-700">
                      {wallet.masterCreditBalance.toFixed(2)}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-sm text-stone-700">
                      {wallet.gameWalletBalance.toFixed(2)}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${SYNC_BADGE[wallet.syncStatus]}`}>
                        {SYNC_LABELS[wallet.syncStatus]}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-sm text-stone-500">
                      {wallet.lastSyncAt ? formatInTimezone(wallet.lastSyncAt, timezone) : '—'}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="inline-flex rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
                        View
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && (
          <div className="flex items-center justify-between text-sm text-stone-500">
            <span>
              {total} wallet{total !== 1 ? 's' : ''}{providerName ? ` for ${providerName}` : ''}
            </span>
            {totalPages > 1 && (
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="rounded border border-stone-200 px-3 py-1 text-sm disabled:opacity-40 hover:bg-stone-50"
                >
                  Previous
                </button>
                <span className="flex items-center px-2">{page} / {totalPages}</span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded border border-stone-200 px-3 py-1 text-sm disabled:opacity-40 hover:bg-stone-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <GameWalletDrawer
        wallet={selectedWallet}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </>
  );
}
